import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { SwitchFieldset } from "ui/switchFieldset";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Badge } from "ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "ui/select";
import { Label } from "ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Clock,
  Briefcase,
  User,
  Eye,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "ui/switch";
import { SearchableSelect } from "ui/searchable-select";
import {
  getCutoffConfigurations,
  createCutoffConfiguration,
  updateCutoffConfiguration,
  deleteCutoffConfiguration, 
  getTourOperatorTypes, 
  getTourOperators,
  getCutoffConfigurationWithDetails, 
  createCutoffDetail,
  updateCutoffDetail,   
  deleteCutoffDetail,
} from "services/cutoffMasterService";
import { useScreenPermission } from "hooks/useScreenPermission";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface GeneralCutoffConfig {
  id: string;
  daysBeforeCutoff: number;
  effectiveFrom: string;
  effectiveTo: string;
  createdAt: string;
  notes?: string;
}

interface TOTypeCutoff {
  id: string;
  generalConfigId: string;
  toTypeId: string;
  toTypeName: string;
  cutoffDays: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

interface IndividualTOCutoff {
  id: string;
  generalConfigId: string;
  toId: string;
  toName: string;
  itsNumber: string;
  cutoffDays: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
}

export const CutoffMasterPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isGeneralDialogOpen, setIsGeneralDialogOpen] = useState(false);
  const [editingGeneralId, setEditingGeneralId] = useState<string | null>(null);
  const [viewingConfigId, setViewingConfigId] = useState<string | null>(null);

  const [toTypes, setToTypes] = useState<
    { id: string; name: string }[]
  >([]);

  const [tourOperators, setTourOperators] = useState<
    { id: string; name: string; itsNumber: string }[]
  >([]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<
    "general" | "toType" | "individualTO" | null
  >(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deletingItem, setDeletingItem] = useState<any>(null);

  const [errors, setErrors] = useState({
    daysBeforeCutoff: "",
    effectiveFrom: "",
    effectiveTo: "",
    notes: "",
  });

  const [toTypeErrors, setToTypeErrors] = useState({
    toTypeId: "",
    cutoffDays: "",
    effectiveFrom: "",
    effectiveTo: "",
  });

  const [individualTOErrors, setIndividualTOErrors] = useState({
    toId: "",
    cutoffDays: "",
    effectiveFrom: "",
    effectiveTo: "",
  });

  // General Cutoff Settings
  const [generalDaysBeforeCutoff, setGeneralDaysBeforeCutoff] =
    useState<number>(0);
  const [generalEffectiveFrom, setGeneralEffectiveFrom] = useState<string>("");
  const [generalEffectiveTo, setGeneralEffectiveTo] = useState<string>("");
  const [generalNotes, setGeneralNotes] = useState<string>("");

  // Saved General Settings
  const [generalConfigs, setGeneralConfigs] = useState<GeneralCutoffConfig[]>(
    [],
  );

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { screenName, icon: ScreenIcon } = useScreenPermission();
   
  const [toTypeCutoffs, setToTypeCutoffs] = useState<TOTypeCutoff[]>([]);
  const [individualTOCutoffs, setIndividualTOCutoffs] = useState<IndividualTOCutoff[]>([]);

  // Detail View State
  const [detailActiveTab, setDetailActiveTab] = useState<
    "to-type" | "individual-to"
  >("to-type");
  const [isTOTypeDialogOpen, setIsTOTypeDialogOpen] = useState(false);
  const [isIndividualTODialogOpen, setIsIndividualTODialogOpen] =
    useState(false);
  const [editingTOTypeId, setEditingTOTypeId] = useState<string | null>(null);
  const [editingIndividualTOId, setEditingIndividualTOId] = useState<
    string | null
  >(null);

  const [, setLoadingDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageHeader({ 
      title: viewingConfigId ? "Cutoff Configuration Detail" : screenName,
      childTitle: viewingConfigId
        ? "Manage TO Type and Individual TO level cutoffs for this configuration"
        : undefined,
      icon: viewingConfigId ? <Clock className="h-5 w-5" /> : <ScreenIcon className="h-6 w-6" />,
      onBack: viewingConfigId ? () => {
        setViewingConfigId(null);
        navigate("/cutoff-master");
      } : undefined,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader, viewingConfigId, navigate]);

  useEffect(() => {
    const fetchCutoffConfigs = async () => {
      try {
        const res = await getCutoffConfigurations(pageNo, pageSize);

        setGeneralConfigs(
          res.items.map((item) => ({
            id: item.publicId,
            daysBeforeCutoff: item.daysBeforeCutoff,
            effectiveFrom: item.effectiveFrom,
            effectiveTo: item.effectiveTo,
            notes: item.notes || "",
            createdAt: "",
          })),
        );

        setTotalCount(res.totalCount);
      } catch (error) {
        toast.error("Failed to load cutoff configurations");
      } finally {
      }
    };

    fetchCutoffConfigs();
  }, [pageNo, pageSize]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapDetails = (res: any) => {
  const toTypeList: TOTypeCutoff[] = [];
  const individualList: IndividualTOCutoff[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  res.details?.forEach((d: any) => {
    const toId = d?.TourOperatorId ?? null;

    if (toId == null || toId === 0) {
      const type = toTypes.find(
        (t) => t.id === String(d?.TourOperatorTypeId)
      );

      toTypeList.push({
        id: d?.PublicId || "",
        generalConfigId: res.publicId,
        toTypeId: String(d?.TourOperatorTypeId || ""),
        toTypeName: type?.name ?? "Unknown",
        cutoffDays: d?.CutoffDays ?? 0,
        effectiveFrom: d?.EffectiveFrom ?? "",
        effectiveTo: d?.EffectiveTo ?? "",
        isActive: d?.IsActive ?? false,
      });
    } else {
      const to = tourOperators.find(
        (t) => t.id === String(toId)
      );

      individualList.push({
        id: d?.PublicId || "",
        generalConfigId: res.publicId,
        toId: d?.TourOperatorId ? String(d.TourOperatorId) : "",
        toName: to?.name ?? "Unknown",
        itsNumber: to?.itsNumber ?? "",
        cutoffDays: d?.CutoffDays ?? 0,
        effectiveFrom: d?.EffectiveFrom ?? "",
        effectiveTo: d?.EffectiveTo ?? "",
        isActive: d?.IsActive ?? false,
      });
    }
  });

  setToTypeCutoffs(toTypeList);
  setIndividualTOCutoffs(individualList);
};


  useEffect(() => {
    const fetchDetails = async () => {
      if (!id || toTypes.length === 0 || tourOperators.length === 0) return;

      try {
        setLoadingDetails(true);

        const res = await getCutoffConfigurationWithDetails(id);

        setViewingConfigId(res.publicId);

        setGeneralDaysBeforeCutoff(res.daysBeforeCutoff);
        setGeneralEffectiveFrom(res.effectiveFrom.split("T")[0]);
        setGeneralEffectiveTo(res.effectiveTo.split("T")[0]);
        setGeneralNotes(res.notes || "");
        setEditingGeneralId(res.publicId);

        mapDetails(res);

      } catch (error) {
        toast.error("Failed to load configuration details");
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, toTypes, tourOperators]);

  useEffect(() => {
    const fetchTOTypes = async () => {
      try {
        const res = await getTourOperatorTypes();

        setToTypes(
          res
            .filter((item) => item.isActive)
            .map((item) => ({
              id: item.tourOperatorTypeId.toString(),
              name: item.typeName,
            }))
        );
      } catch (error) {
        toast.error("Failed to load TO Types");
      }
    };

    fetchTOTypes();
  }, []);

  useEffect(() => {
    const fetchTourOperators = async () => {
      try {
        const res = await getTourOperators();

        setTourOperators(
          res.map((item) => ({
            id: item.tourOperatorId.toString(),
            name: item.officeName, 
            itsNumber: item.itsNumber,
          }))
        );
      } catch (error) {
        toast.error("Failed to load Tour Operators");
      }
    };

    fetchTourOperators();
  }, []);

  useEffect(() => {
  const load = async () => {
    try {
      setLoading(true);

      await Promise.all([
        getCutoffConfigurations(pageNo, pageSize),
        getTourOperatorTypes(),
        getTourOperators(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  load();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const [toTypeFormData, setToTypeFormData] = useState<Partial<TOTypeCutoff>>({
    isActive: true,
  });

  const [individualTOFormData, setIndividualTOFormData] = useState<
    Partial<IndividualTOCutoff>
  >({
    isActive: true,
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // General Config Handlers
  const handleSaveGeneralConfig = async () => {
    const newErrors = {
      daysBeforeCutoff: "",
      effectiveFrom: "",
      effectiveTo: "",
      notes: "",
    };

    if (!generalDaysBeforeCutoff || generalDaysBeforeCutoff < 1) {
      newErrors.daysBeforeCutoff = "Days before cutoff must be greater than 0";
    }

    if (!generalEffectiveFrom) {
      newErrors.effectiveFrom = "Effective From date is required";
    }

    if (!generalEffectiveTo) {
      newErrors.effectiveTo = "Effective To date is required";
    }

    if (
      generalEffectiveFrom &&
      generalEffectiveTo &&
      new Date(generalEffectiveTo) < new Date(generalEffectiveFrom)
    ) {
      newErrors.effectiveTo = "Effective To must be after Effective From";
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) {
      return;
    }

    try {
      const isEdit = !!editingGeneralId;

      if (isEdit) {
        const updatePayload = {
          publicId: editingGeneralId,
          daysBeforeCutoff: generalDaysBeforeCutoff,
          effectiveFrom: new Date(generalEffectiveFrom).toISOString(),
          effectiveTo: new Date(generalEffectiveTo).toISOString(),
          notes: generalNotes || "",
        };

        await updateCutoffConfiguration(updatePayload);
        toast.success("Cutoff Configuration updated successfully");
      } else {
        const createPayload = {
          daysBeforeCutoff: generalDaysBeforeCutoff,
          effectiveFrom: new Date(generalEffectiveFrom).toISOString(),
          effectiveTo: new Date(generalEffectiveTo).toISOString(),
          notes: generalNotes || "",
        };

        await createCutoffConfiguration(createPayload);
        toast.success("Cutoff Configuration created successfully");
      }

      const res = await getCutoffConfigurations(pageNo, pageSize);

      setGeneralConfigs(
        res.items.map((item) => ({
          id: item.publicId,
          daysBeforeCutoff: item.daysBeforeCutoff,
          effectiveFrom: item.effectiveFrom,
          effectiveTo: item.effectiveTo,
          notes: item.notes || "",
          createdAt: "",
        })),
      );

      setTotalCount(res.totalCount);

      handleCloseGeneralDialog();

      if (isEdit) {
        setViewingConfigId(null);
        navigate("/cutoff-master");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to save configuration";

      toast.error(apiMessage);
    }
  };

  const handleEditGeneralConfig = (config: GeneralCutoffConfig) => {
    setViewingConfigId(config.id);
    navigate(`/cutoff-master/${config.id}`);
  };

  const handleDeleteGeneralConfig = (config: GeneralCutoffConfig) => {
    setDeleteType("general");
    setDeletingItem(config);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseGeneralDialog = () => {
    setIsGeneralDialogOpen(false);
    setEditingGeneralId(null);
    setGeneralDaysBeforeCutoff(0);
    setGeneralEffectiveFrom("");
    setGeneralEffectiveTo("");
    setGeneralNotes("");

    setErrors({
      daysBeforeCutoff: "",
      effectiveFrom: "",
      effectiveTo: "",
      notes: "",
    });
  };
  

  const handleViewConfig = (configId: string) => {
    setViewingConfigId(configId);
    navigate(`/cutoff-master/${configId}`);
  };

  // TO Type Level Handlers
  const handleSaveTOType = async () => {
    const newErrors = {
      toTypeId: "",
      cutoffDays: "",
      effectiveFrom: "",
      effectiveTo: "",
    };

    if (!toTypeFormData.toTypeId) {
      newErrors.toTypeId = "Please select TO Type";
    }

    if (!toTypeFormData.cutoffDays || toTypeFormData.cutoffDays < 1) {
      newErrors.cutoffDays = "Cutoff days must be greater than 0";
    }

    setToTypeErrors(newErrors);

    if (Object.values(newErrors).some((e) => e !== "")) return;

    const isDuplicate = toTypeCutoffs.some(
      (item) =>
        item.toTypeId === toTypeFormData.toTypeId &&
        item.id !== editingTOTypeId // allow same record during edit
    );

    if (isDuplicate) {
      toast.error("TO Type already used for another Cutoff");
      return;
    }

    try {
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const payload = {
        masterPublicId: viewingConfigId!,
        tourOperatorTypeId: Number(toTypeFormData.toTypeId),
        tourOperatorId: null,
        cutoffDays: toTypeFormData.cutoffDays!,
        effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
        effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
        isActive: toTypeFormData.isActive ?? true,
      };

      const isEdit = !!editingTOTypeId;

      if (isEdit) {
        await updateCutoffDetail({
          publicId: editingTOTypeId!,
          tourOperatorTypeId: Number(toTypeFormData.toTypeId),
          tourOperatorId: null,
          cutoffDays: toTypeFormData.cutoffDays!,
          effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
          effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
          isActive: toTypeFormData.isActive ?? true,
        });

        toast.success("TO Type cutoff updated successfully");
      } else {
        await createCutoffDetail({
          masterPublicId: viewingConfigId!,
          tourOperatorTypeId: Number(toTypeFormData.toTypeId),
          tourOperatorId: null,
          cutoffDays: toTypeFormData.cutoffDays!,
          effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
          effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
          isActive: toTypeFormData.isActive ?? true,
        });

        toast.success("TO Type cutoff added successfully");
      }

      const res = await getCutoffConfigurationWithDetails(viewingConfigId!);
      mapDetails(res);

      handleCloseTOTypeDialog();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to add TO Type cutoff"
      );
    }
  };

  const handleEditTOType = (cutoff: TOTypeCutoff) => {
    setEditingTOTypeId(cutoff.id);
    setToTypeFormData(cutoff);
    setIsTOTypeDialogOpen(true);
  };

  const handleDeleteTOType = (cutoff: TOTypeCutoff) => {
    setDeleteType("toType");
    setDeletingItem(cutoff);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseTOTypeDialog = () => {
    setIsTOTypeDialogOpen(false);
    setEditingTOTypeId(null);

    setToTypeErrors({
      toTypeId: "",
      cutoffDays: "",
      effectiveFrom: "",
      effectiveTo: "",
    });

    setToTypeFormData({
      cutoffDays: undefined,
      effectiveFrom: viewingConfig?.effectiveFrom || "",
      effectiveTo: viewingConfig?.effectiveTo || "",
      isActive: true,
    });
  };

  // Individual TO Level Handlers
  const handleSaveIndividualTO = async () => {
    const newErrors = {
      toId: "",
      cutoffDays: "",
      effectiveFrom: "",
      effectiveTo: "",
    };

    if (!individualTOFormData.toId) {
      newErrors.toId = "Please select Tour Operator";
    }

    if (!individualTOFormData.cutoffDays || individualTOFormData.cutoffDays < 1) {
      newErrors.cutoffDays = "Cutoff days must be greater than 0";
    }

    setIndividualTOErrors(newErrors);

    if (Object.values(newErrors).some((e) => e !== "")) return;

    const isDuplicate = individualTOCutoffs.some(
      (item) =>
        item.toId === individualTOFormData.toId &&
        item.id !== editingIndividualTOId // allow edit of same row
    );

    if (isDuplicate) {
      toast.error("Tour Operator already used for another Cutoff");
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const payload = {
        masterPublicId: viewingConfigId!,
        tourOperatorTypeId: null,
        tourOperatorId: Number(individualTOFormData.toId),
        cutoffDays: individualTOFormData.cutoffDays!,
        effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
        effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
        isActive: individualTOFormData.isActive ?? true,
      };

     const isEdit = !!editingIndividualTOId;

      if (isEdit) {
        await updateCutoffDetail({
          publicId: editingIndividualTOId!,
          tourOperatorTypeId: null,
          tourOperatorId: Number(individualTOFormData.toId),
          cutoffDays: individualTOFormData.cutoffDays!,
          effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
          effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
          isActive: individualTOFormData.isActive ?? true,
        });

        toast.success("Individual TO cutoff updated successfully");
      } else {
        await createCutoffDetail({
          masterPublicId: viewingConfigId!,
          tourOperatorTypeId: null,
          tourOperatorId: Number(individualTOFormData.toId),
          cutoffDays: individualTOFormData.cutoffDays!,
          effectiveFrom: new Date(viewingConfig!.effectiveFrom).toISOString(),
          effectiveTo: new Date(viewingConfig!.effectiveTo).toISOString(),
          isActive: individualTOFormData.isActive ?? true,
        });

        toast.success("Individual TO cutoff added successfully");
      }

      const res = await getCutoffConfigurationWithDetails(viewingConfigId!);
      mapDetails(res);

      handleCloseIndividualTODialog();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to add Individual TO cutoff"
      );
    }
  };

  const handleEditIndividualTO = (cutoff: IndividualTOCutoff) => {
    setEditingIndividualTOId(cutoff.id);
    setIndividualTOFormData({
      toId: cutoff.toId ? String(cutoff.toId) : "",
      cutoffDays: cutoff.cutoffDays,
      isActive: cutoff.isActive,
    });
    setIsIndividualTODialogOpen(true);
  };

  const handleDeleteIndividualTO = (cutoff: IndividualTOCutoff) => {
    setDeleteType("individualTO");
    setDeletingItem(cutoff);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem || !deleteType) return;

    try {
      if (deleteType === "general") {
        await deleteCutoffConfiguration(deletingItem.id);

        toast.success("General cutoff configuration deleted successfully");

        const res = await getCutoffConfigurations(pageNo, pageSize);

        setGeneralConfigs(
          res.items.map((item) => ({
            id: item.publicId,
            daysBeforeCutoff: item.daysBeforeCutoff,
            effectiveFrom: item.effectiveFrom,
            effectiveTo: item.effectiveTo,
            notes: item.notes || "",
            createdAt: "",
          }))
        );

        setTotalCount(res.totalCount);

        if (viewingConfigId === deletingItem.id) {
          setViewingConfigId(null);
          navigate("/cutoff-master");
        }
      }

     if (deleteType === "toType" || deleteType === "individualTO") {
      await deleteCutoffDetail(deletingItem.id);

      if (deleteType === "toType") {
        toast.success("TO Type cutoff deleted successfully");
      } else if (deleteType === "individualTO") {
        toast.success("Individual TO cutoff deleted successfully");
      }
      
      const res = await getCutoffConfigurationWithDetails(viewingConfigId!);
      mapDetails(res);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Failed to delete configuration";
      toast.error(msg);
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      setDeleteType(null);
    }
  };

  const handleCloseIndividualTODialog = () => {
    setIsIndividualTODialogOpen(false);
    setEditingIndividualTOId(null);

    setIndividualTOErrors({
      toId: "",
      cutoffDays: "",
      effectiveFrom: "",
      effectiveTo: "",
    });

    setIndividualTOFormData({
      cutoffDays: undefined,
      effectiveFrom: viewingConfig?.effectiveFrom || "",
      effectiveTo: viewingConfig?.effectiveTo || "",
      isActive: true,
    });
  };

  const filteredGeneralConfigs = generalConfigs.filter((config) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      config.daysBeforeCutoff.toString().includes(searchLower) ||
      config.notes?.toLowerCase().includes(searchLower) ||
      formatDate(config.effectiveFrom).includes(searchLower) ||
      formatDate(config.effectiveTo).includes(searchLower)
    );
  });

  const filteredTOTypeCutoffs = toTypeCutoffs.filter((cutoff) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      cutoff.toTypeName.toLowerCase().includes(searchLower) ||
      cutoff.cutoffDays.toString().includes(searchLower) ||
      formatDate(cutoff.effectiveFrom).includes(searchLower) ||
      formatDate(cutoff.effectiveTo).includes(searchLower)
    );
  });

  const filteredIndividualTOCutoffs = individualTOCutoffs.filter((cutoff) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      cutoff.toName.toLowerCase().includes(searchLower) ||
      cutoff.itsNumber.toLowerCase().includes(searchLower) ||
      cutoff.cutoffDays.toString().includes(searchLower) ||
      formatDate(cutoff.effectiveFrom).includes(searchLower) ||
      formatDate(cutoff.effectiveTo).includes(searchLower)
    );
  });

  const viewingConfig = viewingConfigId
  ? {
      id: viewingConfigId,
      daysBeforeCutoff: generalDaysBeforeCutoff,
      effectiveFrom: generalEffectiveFrom,
      effectiveTo: generalEffectiveTo,
      notes: generalNotes,
      createdAt: "",
    }
  : null;


  // If viewing a specific config, show detail view
  if (viewingConfigId && viewingConfig) {
    return (
      <div className="space-y-6 pb-8">
        {/* General Config Info Card */}
        <Card className="border-[#5B9BD5]/30" style={{ gap: "0px" }}>
          <CardHeader className="pb-3 border-b bg-gradient-to-r from-sky-50 to-blue-50">
            <CardTitle
              className="text-base flex items-center gap-2"
              style={{ color: "#5B9BD5" }}
            >
              {/* <Clock className="w-5 h-5" /> */}
              General Configuration Details
            </CardTitle>
          </CardHeader>

          <CardContent className="bg-white pt-4 space-y-4">
            {/* ROW 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Cutoff Days"
                  type="number"
                  value={generalDaysBeforeCutoff}
                  onChange={(e) =>
                    setGeneralDaysBeforeCutoff(parseInt(e.target.value) || 0)
                  }
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Effective From"
                  type="date"
                  value={generalEffectiveFrom}
                  onChange={(e) => setGeneralEffectiveFrom(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Effective To"
                  type="date"
                  value={generalEffectiveTo}
                  onChange={(e) => setGeneralEffectiveTo(e.target.value)}
                />
              </div>
            </div>

            {/* ROW 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div className="space-y-2">
                <InputFieldset
                  label="Notes"
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  placeholder="Enter notes"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveGeneralConfig}
                  style={{ background: "#5B9BD5" }}
                  className="text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Update Configuration
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for TO Type and Individual TO */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ padding: "24px"}}>
          <CardContent
            className="p-0 md:p-6"
            style={{ padding: "10px 0px 0px 0px" }}
          >
            <Tabs
              value={detailActiveTab}
              onValueChange={(value) =>
                setDetailActiveTab(value as "to-type" | "individual-to")
              }
            >
              <TabsList className="grid w-full grid-cols-2 bg-gray-100 mb-4">
                <TabsTrigger
                  value="to-type"
                  className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white data-[state=inactive]:text-gray-600 rounded-lg "
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  TO Type Level wise
                </TabsTrigger>
                <TabsTrigger
                  value="individual-to"
                  className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white data-[state=inactive]:text-gray-600 rounded-lg "
                >
                  <User className="w-4 h-4 mr-2" />
                  Individual TO Level wise
                </TabsTrigger>
              </TabsList>

              <TabsContent value="to-type">
                <Card
                  className="border-[#5B9BD5]/30"
                  style={{ borderRadius: "8px", gap: "0px" }}
                >
                  <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 pb-6" style={{ gap: "0px" }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle style={{ color: "#5B9BD5", paddingBottom: "4px" }}>
                          TO Type Cutoff Configurations
                        </CardTitle>
                        <CardDescription>
                          {filteredTOTypeCutoffs.length} configuration
                          {filteredTOTypeCutoffs.length !== 1 ? "s" : ""} found
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setIsTOTypeDialogOpen(true)}
                        style={{ background: "#5B9BD5" }}
                        className="text-white"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add TO Type
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent style={{ padding: "0px 0px 10px 0px" }}>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              TO Type
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Cutoff Days
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Effective From
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Effective To
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Status
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs text-right"
                            >
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredTOTypeCutoffs.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                className="text-center py-8 px-4 text-gray-500 text-sm"
                              >
                                No TO Type cutoff configurations found. Click
                                "Add TO Type" to create one.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredTOTypeCutoffs.map((cutoff) => (
                              <TableRow
                                key={cutoff.id}
                                className="hover:bg-sky-50/30"
                              >
                                <TableCell className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                      <Briefcase className="w-4 h-4" />
                                    </div>
                                    <span className="text-gray-900 text-sm">
                                      {cutoff.toTypeName}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <Badge
                                    variant="outline"
                                    className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                                  >
                                    {cutoff.cutoffDays} days
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm text-gray-700">
                                  {formatDate(cutoff.effectiveFrom)}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm text-gray-700">
                                  {formatDate(cutoff.effectiveTo)}
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <Badge
                                    className={`${cutoff.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                                  >
                                    {cutoff.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEditTOType(cutoff)}
                                      className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                      title="Edit configuration"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteTOType(cutoff)}
                                      className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                      title="Delete configuration"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden divide-y divide-gray-200">
                      {filteredTOTypeCutoffs.length === 0 ? (
                        <div className="text-center py-8 px-4 text-gray-500 text-sm">
                          No TO Type cutoff configurations found. Click "Add TO
                          Type" to create one.
                        </div>
                      ) : (
                        filteredTOTypeCutoffs.map((cutoff) => (
                          <div
                            key={cutoff.id}
                            className="p-4 hover:bg-sky-50/30 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2 flex-1">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Briefcase className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-gray-900">
                                    {cutoff.toTypeName}
                                  </h3>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <Badge
                                      variant="outline"
                                      className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                                    >
                                      {cutoff.cutoffDays} days
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <Badge
                                className={`${cutoff.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5 flex-shrink-0`}
                              >
                                {cutoff.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditTOType(cutoff)}
                                className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                              >
                                <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTOType(cutoff)}
                                className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-8"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Individual TO Level Tab */}
              <TabsContent value="individual-to">
                <Card
                  className="border-[#5B9BD5]/30"
                  style={{ borderRadius: "8px", gap: "0px" }}
                >
                  <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle style={{ color: "#5B9BD5" }}>
                          Individual TO Cutoff Configurations
                        </CardTitle>
                        <CardDescription className="pb-6">
                          {filteredIndividualTOCutoffs.length} configuration
                          {filteredIndividualTOCutoffs.length !== 1 ? "s" : ""}{" "}
                          found
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setIsIndividualTODialogOpen(true)}
                        style={{ background: "#5B9BD5" }}
                        className="text-white"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Individual TO
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent style={{ padding: "0px 0px 10px 0px" }}>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Tour Operator
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Cutoff Days
                            </TableHead>
                            <TableHead
                              style={{ color: "#3e75a9" }}
                              className="px-4 py-3 text-xs"
                            >
                              Effective From
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Effective To
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs"
                            >
                              Status
                            </TableHead>
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs text-right"
                            >
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredIndividualTOCutoffs.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                className="text-center py-8 px-4 text-gray-500 text-sm"
                              >
                                No Individual TO cutoff configurations found.
                                Click "Add Individual TO" to create one.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredIndividualTOCutoffs.map((cutoff) => (
                              <TableRow
                                key={cutoff.id}
                                className="hover:bg-sky-50/30"
                              >
                                <TableCell className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                      <User className="w-4 h-4" />
                                    </div>
                                    <span className="text-gray-900 text-sm">
                                      {cutoff.toName}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <Badge
                                    variant="outline"
                                    className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                                  >
                                    {cutoff.cutoffDays} days
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm text-gray-700">
                                  {formatDate(cutoff.effectiveFrom)}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-sm text-gray-700">
                                  {formatDate(cutoff.effectiveTo)}
                                </TableCell>
                                <TableCell className="px-4 py-3">
                                  <Badge
                                    className={`${cutoff.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                                  >
                                    {cutoff.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleEditIndividualTO(cutoff)
                                      }
                                      className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                      title="Edit configuration"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() =>
                                        handleDeleteIndividualTO(cutoff)
                                      }
                                      className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                      title="Delete configuration"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden divide-y divide-gray-200">
                      {filteredIndividualTOCutoffs.length === 0 ? (
                        <div className="text-center py-8 px-4 text-gray-500 text-sm">
                          No Individual TO cutoff configurations found. Click
                          "Add Individual TO" to create one.
                        </div>
                      ) : (
                        filteredIndividualTOCutoffs.map((cutoff) => (
                          <div
                            key={cutoff.id}
                            className="p-4 hover:bg-sky-50/30 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2 flex-1">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-gray-900">
                                    {cutoff.toName}
                                  </h3>
                                  <p className="text-xs text-gray-600 mt-0.5">
                                    ITS: {cutoff.itsNumber}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <Badge
                                      variant="outline"
                                      className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                                    >
                                      {cutoff.cutoffDays} days
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <Badge
                                className={`${cutoff.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5 flex-shrink-0`}
                              >
                                {cutoff.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditIndividualTO(cutoff)}
                                className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                              >
                                <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteIndividualTO(cutoff)}
                                className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-8"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* TO Type Dialog */}
        <Dialog
          open={isTOTypeDialogOpen}
          onOpenChange={handleCloseTOTypeDialog}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingTOTypeId ? "Edit TO Type Cutoff" : "Add TO Type Cutoff"}
              </DialogTitle>
              <DialogDescription>
                {editingTOTypeId
                  ? "Update TO Type cutoff configuration"
                  : "Add a new TO Type level cutoff configuration"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <SelectFieldset
                  label="TO Type"
                  required
                >
                  <Select
                    value={toTypeFormData.toTypeId}
                    onValueChange={(value) => {
                      setToTypeFormData({ ...toTypeFormData, toTypeId: value });
                      setToTypeErrors({ ...toTypeErrors, toTypeId: "" });
                    }}
                  >
                    <SelectTriggerUI id="toTypeId">
                      <SelectValue placeholder="Select TO type" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {toTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {toTypeErrors.toTypeId && (
                  <p className="text-xs text-red-500">
                    {toTypeErrors.toTypeId}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Cutoff Days"
                    required
                    id="cutoffDays"
                    type="number"
                    min="1"
                    placeholder="e.g., 2"
                    value={toTypeFormData.cutoffDays ?? ""}
                    onChange={(e) => {
                      setToTypeFormData({
                        ...toTypeFormData,
                        cutoffDays: parseInt(e.target.value) || 0,
                      });
                      setToTypeErrors({ ...toTypeErrors, cutoffDays: "" });
                    }}
                  />
                  {toTypeErrors.cutoffDays && (
                    <p className="text-xs text-red-500">
                      {toTypeErrors.cutoffDays}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Number of days before departure to stop accepting bookings
                  </p>
                </div>

                <div className="space-y-2">
                  <SwitchFieldset label="Status">
                    <div className="flex items-center justify-between w-full">
                      <Label
                        htmlFor="toTypeIsActive"
                        className="text-sm cursor-pointer"
                      >
                        {toTypeFormData.isActive ? "Active" : "Inactive"}
                      </Label>
                      <Switch
                        id="toTypeIsActive"
                        checked={toTypeFormData.isActive}
                        onCheckedChange={(checked) =>
                          setToTypeFormData({
                            ...toTypeFormData,
                            isActive: checked,
                          })
                        }
                      />
                    </div>
                  </SwitchFieldset>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Effective From"
                    id="toTypeEffectiveFrom"
                    type="date"
                    value={viewingConfig?.effectiveFrom || ""}
                    readOnly
                    className="text-sm bg-gray-100 cursor-not-allowed"
                  />
                  {toTypeErrors.effectiveFrom && (
                    <p className="text-xs text-red-500">
                      {toTypeErrors.effectiveFrom}
                    </p>
                  )}
                  {/* <p className="text-xs text-gray-500">
                    Must be within:{" "}
                    {viewingConfig
                      ? `${formatDate(viewingConfig.effectiveFrom)} - ${formatDate(viewingConfig.effectiveTo)}`
                      : "General Config dates"}
                  </p> */}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Effective To "
                    id="toTypeEffectiveTo"
                    type="date"
                    value={viewingConfig?.effectiveTo || ""}
                    readOnly
                    className="text-sm bg-gray-100 cursor-not-allowed"
                  />
                  {toTypeErrors.effectiveTo && (
                    <p className="text-xs text-red-500">
                      {toTypeErrors.effectiveTo}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseTOTypeDialog}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTOType}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm"
              >
                {editingTOTypeId ? "Update" : "Add"} Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Individual TO Dialog */}
        <Dialog
          open={isIndividualTODialogOpen}
          onOpenChange={handleCloseIndividualTODialog}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingIndividualTOId
                  ? "Edit Individual TO Cutoff"
                  : "Add Individual TO Cutoff"}
              </DialogTitle>
              <DialogDescription>
                {editingIndividualTOId
                  ? "Update Individual TO cutoff configuration"
                  : "Add a new Individual TO level cutoff configuration"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <SelectFieldset
                  label="Tour Operator"
                  required
                  error={individualTOErrors.toId}
                >
                  <SearchableSelect
                    id="toId"
                    value={individualTOFormData.toId || ""}
                    onValueChange={(value) => {
                      setIndividualTOFormData({
                        ...individualTOFormData,
                        toId: value,
                      });
                      setIndividualTOErrors({ ...individualTOErrors, toId: "" });
                    }}
                    options={tourOperators.map((to) => ({
                      id: to.id,
                      label: `${to.name}`, 
                      searchText: `${to.name}`,
                    }))}
                    placeholder="Select tour operator"
                  />
                </SelectFieldset>
                {individualTOErrors.toId && (
                  <p className="text-xs text-red-500">
                    {individualTOErrors.toId}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Cutoff Days"
                    required
                    id="indCutoffDays"
                    type="number"
                    min="1"
                    placeholder="e.g., 2"
                    value={individualTOFormData.cutoffDays ?? ""}
                    onChange={(e) => {
                      setIndividualTOFormData({
                        ...individualTOFormData,
                        cutoffDays: parseInt(e.target.value) || 0,
                      });
                      setIndividualTOErrors({ ...individualTOErrors, cutoffDays: "" });
                    }}
                  />
                  {individualTOErrors.cutoffDays && (
                    <p className="text-xs text-red-500">
                      {individualTOErrors.cutoffDays}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Number of days before departure to stop accepting bookings
                  </p>
                </div>

                <div className="space-y-2">
                  <SwitchFieldset label="Active Status">
                    <div className="flex items-center justify-between w-full">
                      <Label
                        htmlFor="indTOIsActive"
                        className="text-sm cursor-pointer"
                      >
                        {individualTOFormData.isActive ? "Active" : "Inactive"}
                      </Label>
                      <Switch
                        id="indTOIsActive"
                        checked={individualTOFormData.isActive}
                        onCheckedChange={(checked) =>
                          setIndividualTOFormData({
                            ...individualTOFormData,
                            isActive: checked,
                          })
                        }
                      />
                    </div>
                  </SwitchFieldset>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Effective From "
                    type="date"
                    value={viewingConfig?.effectiveFrom || ""}
                    readOnly
                    className="text-sm bg-gray-100 cursor-not-allowed"
                  />
                  {individualTOErrors.effectiveFrom && (
                    <p className="text-xs text-red-500">
                      {individualTOErrors.effectiveFrom}
                    </p>
                  )}
                  {/* <p className="text-xs text-gray-500">
                    Must be within:{" "}
                    {viewingConfig
                      ? `${formatDate(viewingConfig.effectiveFrom)} - ${formatDate(viewingConfig.effectiveTo)}`
                      : "General Config dates"}
                  </p> */}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Effective To"
                    type="date"
                    value={viewingConfig?.effectiveTo || ""}
                    readOnly
                    className="text-sm bg-gray-100 cursor-not-allowed"
                  />

                  {individualTOErrors.effectiveTo && (
                    <p className="text-xs text-red-500">
                      {individualTOErrors.effectiveTo}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseIndividualTODialog}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveIndividualTO}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm"
              >
                {editingIndividualTOId ? "Update" : "Add"} Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Configuration
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  {deleteType === "general" &&
                    `"${deletingItem?.notes || "General Configuration"}"`}
                  {deleteType === "toType" && `"${deletingItem?.toTypeName}"`}
                  {deleteType === "individualTO" && `"${deletingItem?.toName}"`}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                  setDeleteType(null);
                }}
              >
                No, Cancel
              </Button>

              <Button
                onClick={confirmDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main List View
  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search cutoff configurations..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => {
                handleCloseGeneralDialog();
                setIsGeneralDialogOpen(true);
              }}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add General Cutoff
            </Button>
          </div>
        </div>

        {/* General Cutoff Configurations List */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ marginTop: "10px", gap: "0px" }}>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Cutoff Days
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Effective From
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Effective To
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Notes
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGeneralConfigs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No general cutoff configurations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGeneralConfigs.map((config) => (
                      <TableRow key={config.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-2 py-0.5">
                            {config.daysBeforeCutoff} days
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-gray-700">
                          {formatDate(config.effectiveFrom)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-gray-700">
                          {formatDate(config.effectiveTo)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">
                          {config.notes || "-"}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewConfig(config.id)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="View details"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button> */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditGeneralConfig(config)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit configuration"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteGeneralConfig(config)}
                              className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                              title="Delete configuration"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredGeneralConfigs.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 text-sm">
                  No general cutoff configurations found
                </div>
              ) : (
                filteredGeneralConfigs.map((config) => (
                  <div
                    key={config.id}
                    className="p-4 hover:bg-sky-50/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs px-2 py-1">
                        {config.daysBeforeCutoff} days
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Effective From:</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(config.effectiveFrom)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Effective To:</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(config.effectiveTo)}
                        </span>
                      </div>
                      {config.notes && (
                        <div>
                          <span className="text-gray-600">Notes:</span>
                          <p className="text-gray-900 mt-1">{config.notes}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewConfig(config.id)}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditGeneralConfig(config)}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteGeneralConfig(config)}
                        className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-8"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="cutoff configurations"
          />
        </Card>

        {/* Add/Edit General Configuration Dialog */}
        <Dialog
          open={isGeneralDialogOpen}
          onOpenChange={handleCloseGeneralDialog}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingGeneralId
                  ? "Edit General Cutoff Configuration"
                  : "Add General Cutoff Configuration"}
              </DialogTitle>
              <DialogDescription>
                {editingGeneralId
                  ? "Update general cutoff configuration details"
                  : "Add a new general days before cutoff configuration"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Before Cutoff"
                    required
                    id="generalDaysBeforeCutoff"
                    type="number"
                    min="0"
                    placeholder="e.g., 3"
                    value={generalDaysBeforeCutoff || ""}
                    onChange={(e) => {
                      setGeneralDaysBeforeCutoff(parseInt(e.target.value) || 0);
                      setErrors({ ...errors, daysBeforeCutoff: "" });
                    }}
                    className="text-sm"
                  />

                  {errors.daysBeforeCutoff && (
                    <p className="text-xs text-red-500">
                      {errors.daysBeforeCutoff}
                    </p>
                  )}
                  <p className="text-xs text-gray-600">
                    Additional days to block before cutoff period starts
                  </p>
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Effective From"
                    required
                    id="generalEffectiveFrom"
                    type="date"
                    value={generalEffectiveFrom}
                    onChange={(e) => {
                      setGeneralEffectiveFrom(e.target.value);
                      setErrors({ ...errors, effectiveFrom: "" });
                    }}
                    className="text-sm"
                  />

                  {errors.effectiveFrom && (
                    <p className="text-xs text-red-500">{errors.effectiveFrom}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Effective To"
                    id="generalEffectiveTo"
                    type="date"
                    value={generalEffectiveTo}
                    onChange={(e) => {
                      setGeneralEffectiveTo(e.target.value);
                      setErrors({ ...errors, effectiveTo: "" });
                    }}
                    className="text-sm"
                  />

                  {errors.effectiveTo && (
                    <p className="text-xs text-red-500">{errors.effectiveTo}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Notes"
                  id="generalNotes"
                  placeholder="e.g., Peak season cutoff buffer"
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  className="text-sm"
                />
                <p className="text-xs text-gray-600">
                  Optional description for this configuration
                </p>
              </div>

              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-gray-700 leading-relaxed">
                  <strong className="text-amber-700">How it works:</strong> This
                  setting adds extra days to ALL cutoff configurations globally.
                  For example, if a TO has a 10-day cutoff and you set this to 3
                  days, their effective cutoff becomes 13 days before departure.
                  This applies during the specified date period only.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseGeneralDialog}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGeneralConfig}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm"
              >
                {editingGeneralId ? "Update" : "Add"} Configuration
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Configuration
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  {deleteType === "general" &&
                    `"${deletingItem?.notes || "General Configuration"}"`}
                  {deleteType === "toType" && `"${deletingItem?.toTypeName}"`}
                  {deleteType === "individualTO" && `"${deletingItem?.toName}"`}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                  setDeleteType(null);
                }}
              >
                No, Cancel
              </Button>

              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
